/** P-05 — family members: list + add/edit (modal) + Govt-ID (modal) + remove. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { addFamily, listFamily, removeFamily, setFamilyGovtId, updateFamily } from "../../api/patient";
import type { FamilyMember } from "../../api/patient";
import { describeError } from "../../api/errors";
import { useFormSubmit } from "../../api/useFormSubmit";
import { GOVT_ID_FORMATS, govtIdProblem, normaliseGovtId } from "../../lib/govtIds";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { ConfirmDelete } from "../../components/ui/ConfirmDelete";
import { EditIcon, IconButton, RowActions, TrashIcon } from "../../components/ui/IconButton";
import { Modal } from "../../components/ui/Modal";
import { PhoneInput } from "../../components/ui/PhoneInput";
import { Select } from "../../components/ui/Select";

// The values the API takes, shown as words (ACCT-25).
const RELATIONSHIPS = [
  { value: "spouse", label: "Spouse" },
  { value: "parent", label: "Parent" },
  { value: "child", label: "Child" },
  { value: "sibling", label: "Sibling" },
  { value: "other", label: "Other" },
];
const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const relationshipOf = (m: FamilyMember) =>
  m.relationship_label || RELATIONSHIPS.find((r) => r.value === m.relationship)?.label || m.relationship;

/** Today in the browser's time zone, as a date input's `max` (the API refuses a future birth date). */
function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function Family() {
  const qc = useQueryClient();
  const { data: members = [] } = useQuery({ queryKey: ["family"], queryFn: listFamily });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["family"] });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FamilyMember | null>(null);
  const [govtFor, setGovtFor] = useState<FamilyMember | null>(null);
  // the member whose removal is being confirmed (typing "delete")
  const [removing, setRemoving] = useState<FamilyMember | null>(null);
  const addKey = useIdempotencyKey();

  const remove = useMutation({
    mutationFn: removeFamily,
    onSuccess: invalidate,
    onError: (e) => toast.error(describeError(e)),
    onSettled: () => setRemoving(null),
  });

  return (
    <div className="space-y-5">
      {/* On a phone the button drops under the title rather than squeezing into a two-line pill (RESP-19). */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Family members</h2>
          <p className="text-sm text-gray-500">Dependents you can order prescriptions for.</p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="shrink-0 whitespace-nowrap rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg shadow-xs hover:opacity-90"
        >
          + Add member
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {members.map((m) => (
          <div key={m.id} className="rounded-xl border bg-white p-4 shadow-xs">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 font-medium">
                  {m.full_name}
                  {m.is_minor && <span className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-xs font-normal text-amber-700">minor</span>}
                </div>
                <dl className="mt-1 space-y-0.5 text-xs">
                  <div className="flex gap-1">
                    <dt className="text-gray-400">Relationship:</dt>
                    <dd className="text-gray-600">{relationshipOf(m)}</dd>
                  </div>
                  <div className="flex gap-1">
                    <dt className="text-gray-400">Date of birth:</dt>
                    <dd className="text-gray-600">{m.date_of_birth}</dd>
                  </div>
                </dl>
              </div>
              <RowActions>
                <IconButton label="Edit" tone="brand" onClick={() => { setEditing(m); setFormOpen(true); }}>
                  <EditIcon />
                </IconButton>
                <IconButton label="Remove" tone="danger" onClick={() => setRemoving(m)}>
                  <TrashIcon />
                </IconButton>
              </RowActions>
            </div>
            <div className="mt-3 flex items-center justify-between border-t pt-2 text-sm">
              <span className="text-gray-500">
                Govt ID: {m.govt_id_number_masked ? `${m.govt_id_type} ${m.govt_id_number_masked}` : "not set"}
              </span>
              <button onClick={() => setGovtFor(m)} className="text-brand hover:underline">
                {m.govt_id_number_masked ? "Update" : "Add"}
              </button>
            </div>
          </div>
        ))}
        {members.length === 0 && <p className="text-sm text-gray-400">No family members yet.</p>}
      </div>

      <ConfirmDelete
        open={removing !== null}
        title="Remove family member?"
        busy={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
      >
        <p>
          <b className="text-gray-800">{removing?.full_name}</b> will be removed from your family members.
        </p>
      </ConfirmDelete>

      <Modal open={formOpen} title={editing ? "Edit member" : "Add family member"} onClose={() => setFormOpen(false)}>
        <FamilyForm
          initial={editing}
          onCancel={() => setFormOpen(false)}
          onSubmit={async (vals) => {
            if (editing) {
              // date_of_birth is immutable; only send the editable fields.
              await updateFamily(editing.id, {
                full_name: vals.full_name,
                gender: vals.gender,
                relationship: vals.relationship,
                contact_phone: vals.contact_phone ?? "",
              });
            } else {
              await addFamily(vals, addKey.keyFor(vals));
              addKey.reset();
            }
            setFormOpen(false);
            invalidate();
          }}
        />
      </Modal>

      <Modal open={govtFor !== null} title="Government ID" onClose={() => setGovtFor(null)}>
        {govtFor && (
          <GovtIdForm
            onCancel={() => setGovtFor(null)}
            onSubmit={async (vals) => {
              await setFamilyGovtId(govtFor.id, vals);
              setGovtFor(null);
              invalidate();
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function initialGender(initial: FamilyMember | null): string {
  if (!initial) return "male";
  const g = (initial.gender ?? "").trim().toLowerCase();
  return GENDERS.some((x) => x.value === g) ? g : "";
}

function FamilyForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: FamilyMember | null;
  onSubmit: (v: {
    full_name: string;
    date_of_birth: string;
    gender: string;
    relationship: string;
    contact_phone?: string | null;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const { busy, error, run } = useFormSubmit(onSubmit);
  const [form, setForm] = useState({
    full_name: initial?.full_name ?? "",
    date_of_birth: initial?.date_of_birth ?? "",
    // Stored by an older app as "Male"; one that isn't any of the three must be chosen again.
    gender: initialGender(initial),
    relationship: initial?.relationship ?? "child",
    contact_phone: initial?.contact_phone ?? "",
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void run({ ...form, full_name: form.full_name.trim() });
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Full name">
          <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required minLength={2} maxLength={120} className="w-full rounded-lg border px-3 py-1.5" />
        </Field>
        <Field label={`Date of birth${initial ? " (immutable)" : ""}`}>
          <input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} required disabled={!!initial} min="1900-01-01" max={todayYmd()} className="w-full rounded-lg border px-3 py-1.5 disabled:bg-gray-50" />
        </Field>
        <Field label="Gender">
          <Select value={form.gender} options={GENDERS} onChange={(gender) => setForm({ ...form, gender })} required className="w-full" />
        </Field>
        <Field label="Relationship">
          <Select value={form.relationship} options={RELATIONSHIPS} onChange={(relationship) => setForm({ ...form, relationship })} className="w-full" />
        </Field>
        <Field label="Contact phone (optional)">
          <PhoneInput
            value={form.contact_phone}
            onChange={(v) => setForm({ ...form, contact_phone: v === "+91" ? "" : v })}
          />
        </Field>
      </div>
      {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="flex gap-2">
        <button disabled={busy} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50">
          {busy ? "Saving…" : initial ? "Save" : "Add"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function GovtIdForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (v: { govt_id_type: string; govt_id_number: string; govt_id_belongs_to_guardian: boolean }) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({ govt_id_type: "aadhaar", govt_id_number: "", govt_id_belongs_to_guardian: false });
  const { busy, error, setError, run } = useFormSubmit(onSubmit);
  const format = GOVT_ID_FORMATS.find((f) => f.type === form.govt_id_type);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        // The number in its type's format (ACCT-14) — the API checks the same.
        const problem = govtIdProblem(form.govt_id_type, form.govt_id_number);
        if (problem) return setError(problem);
        void run({ ...form, govt_id_number: normaliseGovtId(form.govt_id_number) });
      }}
      className="space-y-4"
    >
      <Field label="ID type">
        <Select
          value={form.govt_id_type}
          options={GOVT_ID_FORMATS.map((f) => ({ value: f.type, label: f.label }))}
          onChange={(govt_id_type) => setForm({ ...form, govt_id_type })}
          className="w-full"
        />
      </Field>
      <Field label="ID number">
        <input
          value={form.govt_id_number}
          onChange={(e) => setForm({ ...form, govt_id_number: e.target.value })}
          required
          placeholder={format?.example}
          autoCapitalize="characters"
          className="w-full rounded-lg border px-3 py-1.5"
        />
        {format && <span className="text-xs text-gray-400">{format.rule}</span>}
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.govt_id_belongs_to_guardian} onChange={(e) => setForm({ ...form, govt_id_belongs_to_guardian: e.target.checked })} />
        This ID belongs to the guardian (for minors)
      </label>
      <p className="text-xs text-gray-400">Stored encrypted; only the last 4 digits are ever shown.</p>
      {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="flex gap-2">
        <button disabled={busy} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-gray-500">{label}</span>
      {children}
    </label>
  );
}
